import dynamic from 'next/dynamic';

import { useMemo, useState } from 'react';

import classNames from 'classnames';

import { Spinner } from '@uikit';

import { useTokenChartQuery } from '@query';

import { DeserializedTokenData, TokenPeriod } from '@api';

import { formatNumberGroupThousands, formatUsd } from '@shared/helpers/number';

import I18nInfoTooltip from '@store:web/components/common/I18nInfoTooltip/I18nInfoTooltip';
import Tabs from '@store:web/components/common/Tabs/Tabs';

const AppTokenChart = dynamic(() => import('./AppTokenChart'), { ssr: false });
const DEFAULT_PERIOD: TokenPeriod = '1d';

interface AppTokenSectionProps {
  token: DeserializedTokenData;
  tokenSummary: string;
  tokenOverview: string;
  tokenWhitepaper: string;
}

const AppTokenSection = ({
  token,
  tokenOverview,
  tokenSummary,
  tokenWhitepaper,
}: AppTokenSectionProps) => {
  const [period, setPeriod] = useState<TokenPeriod>(DEFAULT_PERIOD);
  const { data: tokenChart, isPending: isTokenChartLoading } = useTokenChartQuery(
    {
      tokenId: token.id,
      searchParams: {
        period,
      },
    },
    {
      enabled: period !== DEFAULT_PERIOD,
    },
  );
  const tokenChartPrices = useMemo(() => {
    const { prices } = tokenChart || token || {};
    return (
      prices?.map((el) => ({
        price: el.attributes.price,
        time: el.attributes.time,
      })) || []
    );
  }, [tokenChart, token]);
  const isLoading = period !== DEFAULT_PERIOD && isTokenChartLoading;

  if (!token) {
    return null;
  }

  const statGroup = [
    [
      {
        label: 'Market Cap',
        content: token.attributes?.marketCap ? formatUsd(token.attributes?.marketCap) : '-',
      },
      {
        label: 'Volume',
        content: token.attributes?.volume24h ? formatUsd(token.attributes?.volume24h) : '-',
      },
      {
        label: 'Circulating Supply',
        content: token.attributes?.circulatingSupply
          ? formatNumberGroupThousands(token.attributes?.circulatingSupply)
          : '-',
      },
    ],
    [
      {
        label: 'Max Supply',
        content: token.attributes?.maxSupply
          ? formatNumberGroupThousands(token.attributes?.maxSupply)
          : '-',
      },
      {
        label: 'FDMC',
        content: token.attributes?.FDMC
          ? formatUsd(token.attributes?.FDMC, { maximumFractionDigits: 0 })
          : '-',
      },
    ],
  ];

  const tabsContent: { name: string; content: string }[] = [
    {
      name: 'Token Overview',
      content: tokenOverview,
    },
    {
      name: 'Summary',
      content: tokenSummary,
    },
    {
      name: 'Whitepaper Summary',
      content: tokenWhitepaper,
    },
  ];

  const showAdditionalInfo =
    token?.attributes.fullname && (tokenOverview || tokenSummary || tokenWhitepaper);

  return (
    <section className="flex flex-col gap-14 sm:gap-7 lg:gap-0 lg:p-0 lg:app-page-section-card">
      <div className="flex gap-14 sm:gap-7 lg:gap-0 max-lg:flex-col ">
        <div className="p-store-container-px -mx-store-container-px sm:m-0 sm:p-app-page-section-card-p sm:max-lg:rounded-xl flex flex-col gap-8 min-w-0 flex-1 bg-primary-100">
          <header className="flex flex-wrap gap-5 justify-between">
            <h2 className="t-section-title" data-qa="token-title">
              {token.attributes.symbol} Token Price Chart (Real Time)
            </h2>

            <ul className="flex gap-1 px-1.5 rounded-full bg-neutral-50 shadow-popover ml-auto">
              {token.attributes.availablePeriods.map((availablePeriod) => {
                const isActive = period === availablePeriod;

                return (
                  <li key={availablePeriod}>
                    <button
                      onClick={() => setPeriod(availablePeriod)}
                      className={classNames(
                        'icon-button uppercase min-w-[2.375rem] min-h-[2.375rem] text-sm p-0 font-medium',
                        isActive
                          ? 'text-secondary cursor-default'
                          : 'text-neutral-600 hover:text-neutral-700',
                      )}
                      data-qa="token-chart-button"
                    >
                      {availablePeriod}
                    </button>
                  </li>
                );
              })}
            </ul>
          </header>
          <div
            className="flex justify-center items-center h-[17.5rem]"
            data-qa="token-chart-container"
          >
            {isLoading && <Spinner color="secondary" />}

            {!isLoading && <AppTokenChart data={tokenChartPrices} period={period} />}
          </div>
        </div>
        <div className="sm:max-lg:app-page-section-card lg:p-app-page-section-card-p flex flex-col gap-8 flex-1">
          <div className="flex">
            <h3 className="t-section-title">
              Token{' '}
              <span className="whitespace-nowrap">
                Statistics{' '}
                <I18nInfoTooltip
                  title="appPage_tokenStatistics_title"
                  content="appPage_tokenStatistics_description"
                  iconClassName="text-neutral-300"
                />
              </span>
            </h3>
          </div>

          <div className="overflow-hidden">
            <div className="-my-6 [&>*]:py-6 divide-y divide-primary-100">
              {statGroup.map((stats, gi) => {
                return (
                  <ul
                    key={gi}
                    className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-6"
                  >
                    {stats.map((stat, si) => {
                      return (
                        <li
                          key={si}
                          className="flex flex-col font-light gap-3"
                          data-qa="token-stat"
                        >
                          <span className="font-semibold" data-qa="token-stat-title">
                            {stat.label}
                          </span>
                          {stat.content}
                        </li>
                      );
                    })}
                  </ul>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showAdditionalInfo && (
        <div className="sm:max-lg:app-page-section-card lg:p-app-page-section-card-p">
          <h3 className="text-lg font-semibold break-words pb-8">
            About {token.attributes.fullname} Token
          </h3>
          <Tabs TabWrapper="h4" tabs={tabsContent} />
        </div>
      )}
    </section>
  );
};

export default AppTokenSection;
